import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { AiConversationFragmentFragment } from 'graphql-common';
import { dateFormat } from '@lib/enums/dateTime';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import CircleLoader, {
  Types as CircleLoaderTypes,
} from '@lib/components/CircleLoader/CircleLoader';
import ActionsMenu from '@lib/components/ActionsMenu/ActionsMenu';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { NavItemTypeEnum } from '@lib/components/Menu/Menu';
import useTypewriter from './hooks/useTypewriter';
import styles from './AiAssistant.module.scss';

const renderRenameIcon = () => <MaterialIcon icon="edit" symbolsOutlined />;
const renderDeleteIcon = () => <MaterialIcon icon="delete" symbolsOutlined />;

// Reveal phases for a freshly-created chat: a loader spins while the title is
// being generated, the streamed title then types in, and finally the meta row
// (messages count + date) fades into place. Existing history items skip straight
// to `ready`.
type Phase = 'loading' | 'typing' | 'ready';

type Props = {
  conversation: AiConversationFragmentFragment;
  isActive: boolean;
  // True only for the chat just created in this session — drives the appear /
  // loader / typewriter animation. Existing history items render statically.
  isNew?: boolean;
  // This chat's first turn ended without success, so the BE never enqueued
  // title generation and none is coming. Ends the title loader on the
  // placeholder instead of spinning forever.
  titleUnavailable?: boolean;
  // This chat's destroy mutation is in flight — swap the actions menu for a
  // loader so it doesn't sit open for the duration of the request.
  isDestroying?: boolean;
  onSelect: (id: string) => void;
  // Requests the rename modal for this chat (handled by the modal host).
  onRename: (id: string) => void;
  onDestroy: (id: string) => void;
};

function ChatHistoryItem(props: Props) {
  const {
    conversation,
    isActive,
    isNew,
    titleUnavailable,
    isDestroying,
    onSelect,
    onRename,
    onDestroy,
  } = props;
  const { t } = useTranslation();

  const { id, title, messagesCount, updatedAt } = conversation;

  // A brand-new chat still awaiting its generated title starts on the loader;
  // anything that already has a title — or whose turn failed, so no title is
  // coming — renders ready.
  const [phase, setPhase] = useState<Phase>(() =>
    isNew && !title && !titleUnavailable ? 'loading' : 'ready',
  );

  // When the streamed title lands on a loading new chat, switch to the
  // typewriter reveal. React 19 "update during render" pattern (no effect).
  const prevTitleRef = useRef(title);
  if (prevTitleRef.current !== title) {
    if (phase === 'loading' && title) setPhase('typing');
    prevTitleRef.current = title;
  }

  // The turn failed while the loader was still spinning: settle on the
  // placeholder title rather than waiting on a title that will never arrive.
  const prevTitleUnavailableRef = useRef(titleUnavailable);
  if (prevTitleUnavailableRef.current !== titleUnavailable) {
    if (titleUnavailable && phase === 'loading') setPhase('ready');
    prevTitleUnavailableRef.current = titleUnavailable;
  }

  const { displayed } = useTypewriter(title || '', {
    enabled: phase === 'typing',
    onDone: () => setPhase('ready'),
  });

  const renderTitle = () => {
    if (phase === 'loading') {
      return (
        <div className={styles.historyItemLoader}>
          <CircleLoader size={20} strokeWidth={2} padding={0} />
        </div>
      );
    }
    if (phase === 'typing') {
      return (
        <Typography
          variant="caption"
          strong
          className={styles.historyItemTitle}
        >
          {displayed}
          <span className={styles.aiTypewriterCaret} />
        </Typography>
      );
    }
    return (
      <Typography variant="caption" strong className={styles.historyItemTitle}>
        {title || t('ai-new-chat')}
      </Typography>
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames(styles.historyItem, {
        [styles.historyItemActive]: isActive,
        [styles.historyItemEntering]: isNew,
      })}
      onClick={() => onSelect(id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onSelect(id);
      }}
    >
      <div className={styles.historyItemBody}>
        {renderTitle()}
        {phase === 'ready' && (
          <div className={styles.historyItemMeta}>
            <div
              className={classNames(styles.historyItemMetaGroup, {
                [styles.historyItemFadeIn]: isNew,
              })}
            >
              <MaterialIcon icon="chat_bubble" symbolsOutlined size="md-12" />
              <Typography variant="caption">{messagesCount}</Typography>
            </div>
            <div
              className={classNames(styles.historyItemDateWrap, {
                [styles.historyItemFadeInDelayed]: isNew,
              })}
            >
              <div className={styles.historyItemDivider} />
              <div className={styles.historyItemMetaGroup}>
                <MaterialIcon
                  icon="calendar_today"
                  symbolsOutlined
                  size="md-12"
                />
                <Typography variant="caption">
                  {getFormattedDate(updatedAt as string, dateFormat)}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
      {phase === 'ready' && (
        <div
          role="presentation"
          className={classNames(styles.historyItemActions, {
            [styles.historyItemActionsVisible]: isDestroying,
          })}
          // Stop the trigger click/keydown from bubbling to the row's
          // onSelect: opening this menu must not re-select (and remount) the
          // conversation, which would wipe unsent composer text and reset pane
          // state — including on the already-active chat.
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {isDestroying ? (
            <div className={styles.historyItemLoadingWrap}>
              <CircleLoader
                size={24}
                strokeWidth={2}
                padding={0}
                type={CircleLoaderTypes.GIF}
              />
            </div>
          ) : (
            <ActionsMenu
              iconButtonType={IconButtonTypes.QuaternaryFilled}
              iconButtonRounded
              actions={[
                {
                  id: 'rename',
                  title: t('ai-rename-chat'),
                  type: NavItemTypeEnum.Secondary,
                  leftIcon: renderRenameIcon,
                  onClick: () => onRename(id),
                },
                {
                  id: 'delete',
                  title: t('ai-delete-chat'),
                  type: NavItemTypeEnum.Error,
                  leftIcon: renderDeleteIcon,
                  onClick: () => onDestroy(id),
                },
              ]}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default ChatHistoryItem;
